import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileContainer, mutationErrorHandler, useToast } from "am-tax-fe-core";

import { queryKeys } from "../queryKeys";

import {
    getEngagementFormDefinitions,
    getProjectFormDefinitions,
    getEngagementFormData,
    getProjectFormData,
    createEngagementDynamicForm,
    createProjectDynamicForm,
    DynamicFormResponse,
    DynamicFormDefinitionResponse,
    DynamicFormUpdateRequest,
    updateDynamicForm,
    updateEngagementDynamicFormData,
    updateProjectDynamicFormData,
    uploadCustomDynamicFormFile,
    CustomDynamicFormResponse,
    createCustomDynamicForm,
    CustomDynamicFormRequest,
    deleteDynamicForm,
} from "./api";

export function useEngagementFormDefinitions(engagementId: string, enabled = true) {
    return useQuery({
        ...queryKeys.questionnaires.engDefinitions(engagementId),
        enabled: enabled && !!engagementId,
        queryFn: () => getEngagementFormDefinitions(engagementId),
    });
}

export function useProjectFormDefinitions(engagementId: string, projectId: string) {
    return useQuery({
        ...queryKeys.questionnaires.projDefintions(engagementId, projectId),
        enabled: !!engagementId && !!projectId,
        queryFn: () => getProjectFormDefinitions(engagementId, projectId),
    });
}

export function useEngagementFormData(engagementId: string, dynamicFormDefinitionId: string, enabled = true) {
    return useQuery({
        //queryKey: ["my-unique-id"],
        ...queryKeys.questionnaires.byFormData(engagementId, "", dynamicFormDefinitionId),
        enabled,
        queryFn: () => getEngagementFormData(engagementId, dynamicFormDefinitionId),
    });
}

export function useProjectFormData(engagementId: string, dynamicFormDefinitionId: string, projectId?: string) {
    const enabled = !!engagementId && !!projectId && !!dynamicFormDefinitionId;
    return useQuery({
        ...queryKeys.questionnaires.byFormData(engagementId, projectId || "", dynamicFormDefinitionId),
        enabled,
        queryFn: () => getProjectFormData(engagementId, projectId ?? "", dynamicFormDefinitionId),
    });
}

export function useCreateEngagementFormData() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: createEngagementDynamicForm,
        onSuccess: async (response: DynamicFormResponse) => {
            toast({ title: "Success", description: "Questionnaire submitted successfully", status: "success", duration: 3000, isClosable: true });
            await queryClient.invalidateQueries(
                queryKeys.questionnaires.byFormData(response.engagementId || "", response.projectId || "", response.dynamicFormDefinitionId || ""),
            );
            await queryClient.invalidateQueries(queryKeys.questionnaires.engDefinitions(response.engagementId || ""));
        },
        ...mutationErrorHandler(toast, "Unable to create engagement dynamic form"),
    });
}

export function useCreateProjectFormData() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: createProjectDynamicForm,
        onSuccess: async (response: DynamicFormResponse) => {
            toast({ title: "Success", description: "Questionnaire submitted successfully", status: "success", duration: 3000, isClosable: true });
            await queryClient.invalidateQueries(
                queryKeys.questionnaires.byFormData(response.engagementId || "", response.projectId || "", response.dynamicFormDefinitionId || ""),
            );
            await queryClient.invalidateQueries(queryKeys.questionnaires.projDefintions(response.engagementId || "", response.projectId || ""));
        },
        ...mutationErrorHandler(toast, "Unable to create project dynamic form"),
    });
}

export function useUpdateEngagementFormData() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: updateEngagementDynamicFormData,
        onSuccess: async (response: DynamicFormResponse) => {
            toast({ title: "Success", description: "Questionnaire updated successfully", status: "success", duration: 3000, isClosable: true });
            await queryClient.invalidateQueries(
                queryKeys.questionnaires.byFormData(response.engagementId || "", response.projectId || "", response.dynamicFormDefinitionId || ""),
            );
            await queryClient.invalidateQueries(queryKeys.questionnaires.engDefinitions(response.engagementId || ""));
        },
        ...mutationErrorHandler(toast, "Unable to update engagement dynamic form"),
    });
}

export function useUpdateProjectFormData() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: updateProjectDynamicFormData,
        onSuccess: async (response: DynamicFormResponse) => {
            toast({ title: "Success", description: "Questionnaire updated successfully", status: "success", duration: 3000, isClosable: true });
            await queryClient.invalidateQueries(
                queryKeys.questionnaires.byFormData(response.engagementId || "", response.projectId || "", response.dynamicFormDefinitionId || ""),
            );
            await queryClient.invalidateQueries(queryKeys.questionnaires.projDefintions(response.engagementId || "", response.projectId || ""));
        },
        ...mutationErrorHandler(toast, "Unable to update project dynamic form"),
    });
}
export function useUpdateQuestionnaireForm(engagementId: string, projectId: string | undefined) {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async ({ dynamicFormId, data }: { dynamicFormId: string; data: DynamicFormUpdateRequest }) => {
            return await updateDynamicForm(engagementId, projectId, dynamicFormId, data);
        },
        onSuccess: async (response: DynamicFormDefinitionResponse) => {
            toast({ title: "Success", description: "Questionnaire updated successfully", status: "success", duration: 3000, isClosable: true });
            if (projectId != undefined) {
                await queryClient.invalidateQueries(queryKeys.questionnaires.projDefintions(response.engagementId || "", response.projectId || ""));
            } else {
                await queryClient.invalidateQueries(queryKeys.questionnaires.engDefinitions(response.engagementId || ""));
            }
        },
        ...mutationErrorHandler(toast, "Unable to update dynamic form"),
    });
}

export function useCreateCustomDynamicForm(engagementId: string, projectId: string | undefined) {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async ({ data }: { data: CustomDynamicFormRequest }) => {
            return await createCustomDynamicForm(engagementId, projectId, data);
        },
        onSuccess: async (response: CustomDynamicFormResponse) => {
            //   toast({ title: "Success", description: "Questionnaire Created Successfully", status: "success", duration: 3000, isClosable: true });
            if (response.projectId != undefined) {
                await queryClient.invalidateQueries(queryKeys.questionnaires.projDefintions(response.engagementId, response.projectId));
            } else {
                await queryClient.invalidateQueries(queryKeys.questionnaires.engDefinitions(response.engagementId));
            }
        },
        ...mutationErrorHandler(toast, "Unable to create dynamic form"),
    });
}

export function useUploadCustomQuestionnaireData(engagementId: string, projectId: string | undefined) {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async ({ fileContainer, customFormId }: { fileContainer: FileContainer; customFormId: string }) => {
            return await uploadCustomDynamicFormFile(engagementId, projectId, customFormId, fileContainer);
        },
        onSuccess: async () => {
            toast({ title: "Success", description: "We have received your questionnaire template file.", status: "success", duration: 3000, isClosable: true });
            setTimeout(async () => {
                if (projectId != undefined) {
                    await queryClient.invalidateQueries(queryKeys.questionnaires.projDefintions(engagementId, projectId));
                } else {
                    await queryClient.invalidateQueries(queryKeys.questionnaires.engDefinitions(engagementId));
                }
            }, 10000);
        },
        ...mutationErrorHandler(toast, "Unable to upload form template"),
    });
}
export function useDeleteQuestionnaireForm(engagementId: string, projectId: string | undefined) {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async ({ dynamicFormId }: { dynamicFormId: string }) => {
            return await deleteDynamicForm(engagementId, projectId, dynamicFormId);
        },
        onSuccess: async () => {
            toast({ title: "Success", description: "Questionnaire deleted successfully", status: "success", duration: 3000, isClosable: true });
            if (projectId != undefined) {
                await queryClient.invalidateQueries(queryKeys.questionnaires.projDefintions(engagementId || "", projectId || ""));
            } else {
                await queryClient.invalidateQueries(queryKeys.questionnaires.engDefinitions(engagementId || ""));
            }
        },
        ...mutationErrorHandler(toast, "Unable to delete dynamic form"),
    });
}
